import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useFollowUpHistory } from '@/hooks/useFollowUps'
import type {
  FollowUpHistoryEventRow,
  FollowUpHistoryMode,
  FollowUpHistorySource,
  FollowUpHistoryStaff,
  FollowUpQueue,
} from '@/lib/api-client'
import { api } from '@/lib/api-client'
import { arrayToCSV, downloadFile } from '@/lib/export'
import { cn, formatCurrency } from '@/lib/utils'
import { ChevronDown, ChevronLeft, ChevronRight, Download, Info, Loader2 } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import {
  ACTION_LABELS,
  QUEUE_META,
  QUEUE_ORDER,
  STAFF_ACTION_TYPES,
  STAFF_ROLE_LABELS,
  eventLabel,
  sourceLabel,
} from './queue-meta'

interface HistoryTabProps {
  branchId: number | undefined
}

function monthOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })
    opts.push({ value, label })
  }
  return opts
}

function fmtDateTime(date: string): string {
  return new Date(date).toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  })
}

function fmtDay(date: string): string {
  return new Date(date).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    timeZone: 'Asia/Kuala_Lumpur',
  })
}

function pct(part: number, whole: number): string {
  if (whole === 0) return '0%'
  return `${Math.round((part / whole) * 1000) / 10}%`
}

/** Definitions shown as tooltips on the KPI tiles (mirror the API doc comment). */
const KPI_DEFINITIONS = {
  followUps:
    'Follow-ups queued in the month, plus (in "Outcomes in month") any older follow-up that was contacted, booked or repurchased during the month.',
  contacted:
    'Follow-ups with at least one action logged by staff: WhatsApp opened or sent, called, replied, no answer, call back, thinking, not interested, blacklisted.',
  bookedAfterContact:
    'An appointment appeared in Avixo (nightly sync) after staff contacted the patient on this follow-up. Detected automatically; staff do not need to mark it.',
  bookedAny:
    'An appointment appeared after the follow-up was opened, whether or not anyone contacted the patient. Includes patients who booked on their own.',
  repurchased: 'A paid invoice dated on or after the follow-up opened.',
  recoveredValue: 'Sum of those repurchase invoices.',
  mode: {
    outcome:
      'Counts contacts, bookings and purchases by the date they happened. A September follow-up booked on 6 Oct counts in October.',
    queued:
      'Cohort view: every follow-up created in the month, with lifetime outcomes for that cohort.',
  },
} as const

const EVENT_DOT: Partial<Record<string, string>> = {
  wa_sent: 'bg-[#25d366]',
  wa_opened: 'bg-[#25d366]/60',
  called: 'bg-info',
  replied: 'bg-success',
  no_answer: 'bg-warning',
  callback: 'bg-warning',
  thinking: 'bg-warning',
  not_interested: 'bg-destructive',
  blacklisted: 'bg-destructive',
  booked: 'bg-success',
  repurchased: 'bg-success',
  expired: 'bg-text-muted',
}

const STAFF_PREVIEW_COUNT = 5
const EVENTS_PAGE_SIZE = 50

export function HistoryTab({ branchId }: HistoryTabProps) {
  const months = React.useMemo(monthOptions, [])
  const [month, setMonth] = React.useState(months[0]?.value ?? '')
  const [mode, setMode] = React.useState<FollowUpHistoryMode>('outcome')
  const [staffId, setStaffId] = React.useState<number | undefined>(undefined)
  const [queue, setQueue] = React.useState<FollowUpQueue | undefined>(undefined)
  const [source, setSource] = React.useState<FollowUpHistorySource>('manual')
  const [hideNoise, setHideNoise] = React.useState(true)
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [page, setPage] = React.useState(1)
  const [logOpen, setLogOpen] = React.useState(true)
  const [showAllStaff, setShowAllStaff] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset paging whenever any filter changes
  React.useEffect(() => {
    setPage(1)
  }, [branchId, month, mode, staffId, queue, source, hideNoise])

  const params = {
    branchId,
    month,
    mode,
    staffId,
    queue,
    source,
    hideNoise,
    search: debouncedSearch || undefined,
    page,
    pageSize: EVENTS_PAGE_SIZE,
  }
  const { data, isPending, isFetching } = useFollowUpHistory(params)

  // Staff dropdown options: remember the unfiltered list so picking one staff
  // member doesn't collapse the dropdown to a single entry.
  const staffOptionsRef = React.useRef<{ id: number; name: string }[]>([])
  if (data && !staffId) {
    staffOptionsRef.current = data.byStaff.map((s) => ({ id: s.userId, name: s.name }))
  }
  const staffOptions = staffOptionsRef.current

  const totals = data?.totals
  const staff = data?.byStaff ?? []
  const visibleStaff = showAllStaff ? staff : staff.slice(0, STAFF_PREVIEW_COUNT)
  const byQueue = React.useMemo(() => {
    const map = new Map((data?.byQueue ?? []).map((q) => [q.queue, q]))
    return QUEUE_ORDER.map((q) => map.get(q)).filter((q): q is NonNullable<typeof q> => !!q)
  }, [data?.byQueue])
  const actionsByType = React.useMemo(() => {
    const map = new Map((data?.actions.byType ?? []).map((a) => [a.type, a.count]))
    return STAFF_ACTION_TYPES.map((type) => ({ type, count: map.get(type) ?? 0 })).filter(
      (a) => a.count > 0
    )
  }, [data?.actions.byType])
  const maxAction = Math.max(1, ...actionsByType.map((a) => a.count))

  const events = data?.events
  const totalPages = events ? Math.max(1, Math.ceil(events.total / EVENTS_PAGE_SIZE)) : 1

  const monthLabel = months.find((m) => m.value === month)?.label ?? month

  const handleExportSummary = () => {
    if (!data) return
    const staffCsv = arrayToCSV(
      data.byStaff.map((s) => ({
        section: 'staff',
        name: s.name,
        role: STAFF_ROLE_LABELS[s.role] ?? s.role,
        branches: s.branches ?? '',
        assigned: s.assigned,
        contacted: s.contacted,
        booked_after_contact: s.bookedAfterContact,
        repurchased: s.repurchased,
        recovered_rm: s.recoveredValue,
        open_untouched: s.openUntouched,
      })),
      [
        { key: 'section', header: 'Section' },
        { key: 'name', header: 'Staff' },
        { key: 'role', header: 'Role' },
        { key: 'branches', header: 'Branches' },
        { key: 'assigned', header: 'Assigned' },
        { key: 'contacted', header: 'Contacted' },
        { key: 'booked_after_contact', header: 'Booked after contact' },
        { key: 'repurchased', header: 'Repurchased' },
        { key: 'recovered_rm', header: 'RM recovered' },
        { key: 'open_untouched', header: 'Open, untouched' },
      ]
    )
    const queueCsv = arrayToCSV(
      [
        ...byQueue.map((q) => ({
          queue: QUEUE_META[q.queue].label,
          follow_ups: q.followUps,
          contacted: q.contacted,
          booked_after_contact: q.bookedAfterContact,
          booked_any: q.bookedAny,
          repurchased: q.repurchased,
          recovered_rm: q.recoveredValue,
        })),
        {
          queue: 'Total',
          follow_ups: data.totals.followUps,
          contacted: data.totals.contacted,
          booked_after_contact: data.totals.bookedAfterContact,
          booked_any: data.totals.bookedAny,
          repurchased: data.totals.repurchased,
          recovered_rm: data.totals.recoveredValue,
        },
      ],
      [
        { key: 'queue', header: 'Queue' },
        { key: 'follow_ups', header: 'Follow-ups' },
        { key: 'contacted', header: 'Contacted' },
        { key: 'booked_after_contact', header: 'Booked after contact' },
        { key: 'booked_any', header: 'Booked, any' },
        { key: 'repurchased', header: 'Repurchased' },
        { key: 'recovered_rm', header: 'RM recovered' },
      ]
    )
    const header = `"Follow-ups summary","${monthLabel}","${mode === 'outcome' ? 'Outcomes in month' : 'Queued in month'}"\n\n`
    downloadFile(
      `${header}${staffCsv}\n\n${queueCsv}`,
      `follow-up-summary-${month}.csv`,
      'text/csv;charset=utf-8;'
    )
  }

  const handleExportEvents = async () => {
    setExporting(true)
    try {
      // Pull every page of the current log filter (max 500 per request).
      const all: FollowUpHistoryEventRow[] = []
      let p = 1
      let total = Number.POSITIVE_INFINITY
      while (all.length < total && p <= 40) {
        const res = await api.followUps.history({ ...params, page: p, pageSize: 500 })
        all.push(...res.events.data)
        total = res.events.total
        if (res.events.data.length === 0) break
        p++
      }
      const csv = arrayToCSV(
        all.map((ev) => ({
          when: fmtDateTime(ev.createdAt),
          patient: ev.patientName,
          queue: QUEUE_META[ev.queue].label,
          action: eventLabel(ev.type, ev.source),
          source: sourceLabel(ev.source),
          by: ev.userName ?? '',
          note: ev.note ?? '',
          result: resultText(ev),
        })),
        [
          { key: 'when', header: 'When' },
          { key: 'patient', header: 'Patient' },
          { key: 'queue', header: 'Queue' },
          { key: 'action', header: 'Action' },
          { key: 'source', header: 'Source' },
          { key: 'by', header: 'By' },
          { key: 'note', header: 'Note' },
          { key: 'result', header: 'Result so far' },
        ]
      )
      downloadFile(csv, `follow-up-log-${month}-${source}.csv`, 'text/csv;charset=utf-8;')
    } catch (err) {
      toast.error((err as Error).message || 'Failed to export event log')
    } finally {
      setExporting(false)
    }
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-5">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="inline-flex rounded-full border border-border bg-card p-0.5">
            {(
              [
                { key: 'outcome', label: 'Outcomes in month' },
                { key: 'queued', label: 'Queued in month' },
              ] as const
            ).map((opt) => (
              <Tooltip key={opt.key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setMode(opt.key)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                      mode === opt.key
                        ? 'bg-info/15 text-info'
                        : 'text-text-muted hover:text-text-dark'
                    )}
                  >
                    {opt.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                  {KPI_DEFINITIONS.mode[opt.key]}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>

          <Select
            value={staffId ? String(staffId) : 'all'}
            onValueChange={(v) => setStaffId(v === 'all' ? undefined : Number(v))}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staffOptions.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={queue ?? 'all'}
            onValueChange={(v) => setQueue(v === 'all' ? undefined : (v as FollowUpQueue))}
          >
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="All queues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All queues</SelectItem>
              {QUEUE_ORDER.map((q) => (
                <SelectItem key={q} value={q}>
                  {QUEUE_META[q].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1" />

          {isFetching && !isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-text-muted" aria-label="Refreshing" />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" disabled={!data || exporting}>
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportSummary}>
                Summary CSV
                <span className="ml-2 text-xs text-text-muted">staff + queues</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportEvents}>
                Event log CSV
                <span className="ml-2 text-xs text-text-muted">current log filter</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {isPending || !totals ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
            Loading history...
          </div>
        ) : (
          <>
            {/* ---------------- Summary ---------------- */}
            <section>
              <SectionHeading
                title="Summary"
                hint={`${monthLabel} · ${mode === 'outcome' ? 'by date it happened' : 'queued in month'}`}
              />
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
                <KpiTile
                  label="Follow-ups"
                  value={totals.followUps}
                  sub={`${totals.open} still open · ${totals.expired} expired`}
                  definition={KPI_DEFINITIONS.followUps}
                />
                <KpiTile
                  label="Contacted by staff"
                  value={totals.contacted}
                  sub={`${pct(totals.contacted, totals.followUps)} of follow-ups`}
                  definition={KPI_DEFINITIONS.contacted}
                />
                <KpiTile
                  label="Booked after contact"
                  value={totals.bookedAfterContact}
                  sub={`${pct(totals.bookedAfterContact, totals.contacted)} of contacted`}
                  definition={KPI_DEFINITIONS.bookedAfterContact}
                  hero
                />
                <KpiTile
                  label="Booked, any"
                  value={totals.bookedAny}
                  sub={`incl. ${Math.max(0, totals.bookedAny - totals.bookedAfterContact)} without a logged contact`}
                  definition={KPI_DEFINITIONS.bookedAny}
                />
                <KpiTile
                  label="Repurchased"
                  value={totals.repurchased}
                  sub="paid invoice after follow-up"
                  definition={KPI_DEFINITIONS.repurchased}
                />
                <KpiTile
                  label="RM recovered"
                  value={formatCurrency(totals.recoveredValue)}
                  sub="sum of repurchase invoices"
                  definition={KPI_DEFINITIONS.recoveredValue}
                />
              </div>
            </section>

            {/* ---------------- Staff activity ---------------- */}
            <section>
              <SectionHeading
                title="Staff activity"
                hint="grouped by who logged the action, not who was assigned"
              />
              {staff.length === 0 ? (
                <EmptyCard>No staff activity for the selected filters.</EmptyCard>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                    {visibleStaff.map((s) => (
                      <StaffCard key={s.userId} staff={s} />
                    ))}
                  </div>
                  {staff.length > STAFF_PREVIEW_COUNT && (
                    <button
                      type="button"
                      onClick={() => setShowAllStaff((v) => !v)}
                      className="mt-2 text-xs font-semibold text-info hover:underline"
                    >
                      {showAllStaff
                        ? 'Show fewer'
                        : `Show all ${staff.length} staff (${staff.length - STAFF_PREVIEW_COUNT} more, incl. assignees with no activity)`}
                    </button>
                  )}
                </>
              )}
            </section>

            {/* ---------------- Queue table + recorded actions ---------------- */}
            <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-[1.25fr_1fr]">
              <div className="rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <h3 className="text-sm font-bold">Outcomes by queue</h3>
                  <span className="text-xs text-text-muted">{totals.followUps} follow-ups</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-bold uppercase text-text-muted">
                        <th className="px-4 py-2">Queue</th>
                        <th className="px-3 py-2 text-right">Follow-ups</th>
                        <th className="px-3 py-2 text-right">Contacted</th>
                        <th className="px-3 py-2 text-right">Booked after</th>
                        <th className="px-3 py-2 text-right">Booked any</th>
                        <th className="px-3 py-2 text-right">Repurch.</th>
                        <th className="px-4 py-2 text-right">RM recovered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byQueue.map((q) => (
                        <tr key={q.queue} className="border-t border-border-light">
                          <td className="px-4 py-2">
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold',
                                QUEUE_META[q.queue].badgeClassName
                              )}
                            >
                              {QUEUE_META[q.queue].label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{q.followUps}</td>
                          <td className="px-3 py-2 text-right font-mono">{q.contacted}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-success">
                            {q.bookedAfterContact}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{q.bookedAny}</td>
                          <td className="px-3 py-2 text-right font-mono">{q.repurchased}</td>
                          <td className="px-4 py-2 text-right font-mono">
                            {formatCurrency(q.recoveredValue)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-border bg-background/40 font-bold text-text-muted">
                        <td className="px-4 py-2">Total</td>
                        <td className="px-3 py-2 text-right font-mono">{totals.followUps}</td>
                        <td className="px-3 py-2 text-right font-mono">{totals.contacted}</td>
                        <td className="px-3 py-2 text-right font-mono text-success">
                          {totals.bookedAfterContact}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{totals.bookedAny}</td>
                        <td className="px-3 py-2 text-right font-mono">{totals.repurchased}</td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatCurrency(totals.recoveredValue)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <h3 className="text-sm font-bold">What staff recorded</h3>
                  <span className="text-xs text-text-muted">
                    {data?.actions.total ?? 0} actions · {data?.actions.withOutcome ?? 0} with an
                    outcome
                  </span>
                </div>
                <div className="space-y-2.5 p-4">
                  {actionsByType.map((a) => (
                    <div
                      key={a.type}
                      className="grid grid-cols-[130px_1fr_48px] items-center gap-2.5 text-[12.5px]"
                    >
                      <span className="truncate">{ACTION_LABELS[a.type]}</span>
                      <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            a.type === 'not_interested' || a.type === 'blacklisted'
                              ? 'bg-destructive'
                              : a.type === 'replied'
                                ? 'bg-success'
                                : 'bg-info'
                          )}
                          style={{ width: `${Math.max(2, (a.count / maxAction) * 100)}%` }}
                        />
                      </div>
                      <span className="text-right font-mono text-xs">{a.count}</span>
                    </div>
                  ))}
                  {actionsByType.length === 0 && (
                    <p className="text-sm text-text-muted">No staff actions recorded this month.</p>
                  )}
                </div>
                <div className="border-t border-border px-4 py-2 text-[11.5px] text-text-muted">
                  Counts actions, not patients. A patient can have several.
                </div>
              </div>
            </div>

            {/* ---------------- Event log (collapsed) ---------------- */}
            <Collapsible open={logOpen} onOpenChange={setLogOpen}>
              <div className="rounded-xl border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-hover rounded-xl"
                  >
                    <div>
                      <h3 className="text-sm font-bold">Event log</h3>
                      <p className="text-xs text-text-muted">
                        {events?.counts.manual ?? 0} staff actions · {events?.counts.system ?? 0}{' '}
                        system detected · {monthLabel}
                      </p>
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-text-muted transition-transform',
                        logOpen && 'rotate-180'
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2.5">
                    <div className="inline-flex rounded-full border border-border bg-background p-1">
                      {(
                        [
                          { key: 'manual', label: 'Staff actions', count: events?.counts.manual },
                          { key: 'system', label: 'System detected', count: events?.counts.system },
                          { key: 'all', label: 'All', count: events?.counts.all },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setSource(opt.key)}
                          className={cn(
                            'rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                            source === opt.key
                              ? 'bg-info/15 text-info'
                              : 'text-text-muted hover:text-text-dark'
                          )}
                        >
                          {opt.label}
                          {opt.count !== undefined && (
                            <span className="ml-1.5 font-mono text-xs opacity-70">{opt.count}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <label
                      htmlFor="history-hide-noise"
                      className="ml-1 inline-flex items-center gap-2 text-xs text-text-muted"
                    >
                      <Checkbox
                        id="history-hide-noise"
                        checked={hideNoise}
                        onCheckedChange={(v) => setHideNoise(v === true)}
                      />
                      Hide &ldquo;Entered queue&rdquo; and &ldquo;Assigned&rdquo;
                    </label>
                    <div className="flex-1" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search patient / PNO"
                      className="h-8 w-[200px] text-xs"
                    />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-sm">
                      <thead>
                        <tr className="text-left text-[11px] font-bold uppercase text-text-muted">
                          <th className="px-4 py-2">When</th>
                          <th className="px-3 py-2">Patient</th>
                          <th className="px-3 py-2">Queue</th>
                          <th className="px-3 py-2">Action</th>
                          <th className="px-3 py-2">By</th>
                          <th className="px-3 py-2">Note</th>
                          <th className="px-4 py-2">Result so far</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(events?.data ?? []).map((ev) => (
                          <tr key={ev.id} className="border-t border-border-light align-top">
                            <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-text-muted">
                              {fmtDateTime(ev.createdAt)}
                            </td>
                            <td className="px-3 py-2 font-semibold">{ev.patientName}</td>
                            <td className="px-3 py-2">
                              <span
                                className={cn(
                                  'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold',
                                  QUEUE_META[ev.queue].badgeClassName
                                )}
                              >
                                {QUEUE_META[ev.queue].label}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              <span className="inline-flex items-center gap-1.5">
                                <span
                                  className={cn(
                                    'h-2 w-2 rounded-full',
                                    EVENT_DOT[ev.type] ?? 'bg-text-light'
                                  )}
                                />
                                {eventLabel(ev.type, ev.source)}
                                {ev.source !== 'manual' && (
                                  <span className="rounded bg-success/10 px-1 py-px text-[10px] font-extrabold tracking-wide text-success">
                                    {sourceLabel(ev.source).replace('AUTO · ', '')}
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {ev.userName ?? <span className="text-text-muted">System</span>}
                            </td>
                            <td className="max-w-[260px] px-3 py-2 text-xs text-text-muted">
                              {ev.note ?? '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-xs">
                              <ResultBadge ev={ev} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {events && events.data.length === 0 && (
                      <p className="p-6 text-center text-sm text-text-muted">
                        No events for the selected filters.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2.5 text-xs text-text-muted">
                    <span>
                      {events
                        ? `${(page - 1) * EVENTS_PAGE_SIZE + 1}–${Math.min(page * EVENTS_PAGE_SIZE, events.total)} of ${events.total}`
                        : '—'}
                      {hideNoise && ' · Entered queue / Assigned hidden'}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <span className="font-mono">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </>
        )}
      </div>
    </TooltipProvider>
  )
}

// ----------------------------------------------------------------------------
// Pieces
// ----------------------------------------------------------------------------

function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2">
      <h2 className="text-sm font-extrabold">{title}</h2>
      {hint && <span className="text-xs text-text-muted">{hint}</span>}
    </div>
  )
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-text-muted">
      {children}
    </div>
  )
}

function KpiTile({
  label,
  value,
  sub,
  definition,
  hero,
}: {
  label: string
  value: number | string
  sub: string
  definition: string
  hero?: boolean
}) {
  return (
    <div
      className={cn(
        'relative rounded-2xl border p-3.5',
        hero
          ? 'border-success/40 bg-gradient-to-b from-success/10 to-card'
          : 'border-border bg-card'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-wide text-text-muted">
          {label}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="-mr-1 -mt-1 rounded p-1 text-text-light hover:text-text-muted"
              aria-label={`What does ${label} mean?`}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
            {definition}
          </TooltipContent>
        </Tooltip>
      </div>
      <div
        className={cn(
          'mt-1 text-2xl font-extrabold tracking-tight',
          hero ? 'text-success' : 'text-text-dark'
        )}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11.5px] leading-snug text-text-muted">{sub}</div>
    </div>
  )
}

function StaffCard({ staff }: { staff: FollowUpHistoryStaff }) {
  const roleLabel = STAFF_ROLE_LABELS[staff.role] ?? staff.role
  const bookedRate = staff.contacted > 0 ? pct(staff.bookedAfterContact, staff.contacted) : null
  const initials = staff.name
    .replace(/^Dr\.?\s+/i, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
  return (
    <div className="rounded-2xl border border-border bg-card p-3.5">
      <div className="mb-2.5 flex items-start gap-2">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-extrabold">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-bold">{staff.name}</span>
            <span
              className={cn(
                'ml-auto shrink-0 rounded border px-1.5 py-px text-[10px] font-bold uppercase tracking-wide',
                staff.role === 'doctor'
                  ? 'border-chart-4/40 text-chart-4'
                  : 'border-border text-text-muted'
              )}
            >
              {roleLabel}
            </span>
          </div>
          {staff.branches && (
            <div className="truncate text-[11px] text-text-muted">{staff.branches}</div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded-lg bg-muted p-2">
          <div className="text-[9.5px] font-bold uppercase text-text-muted">Contacted</div>
          <div className="text-lg font-extrabold">{staff.contacted}</div>
        </div>
        <div className="rounded-lg bg-muted p-2">
          <div className="text-[9.5px] font-bold uppercase text-text-muted">Booked after</div>
          <div className="text-lg font-extrabold text-success">{staff.bookedAfterContact}</div>
        </div>
        <div className="rounded-lg bg-muted p-2">
          <div className="text-[9.5px] font-bold uppercase text-text-muted">Repurch.</div>
          <div className="text-lg font-extrabold">{staff.repurchased}</div>
        </div>
      </div>
      <div className="mt-2.5 text-[11.5px] leading-relaxed text-text-muted">
        {bookedRate !== null ? (
          <>
            Booked rate <strong className="text-text-dark">{bookedRate}</strong>
            {' · '}
          </>
        ) : null}
        recovered <strong className="text-text-dark">{formatCurrency(staff.recoveredValue)}</strong>
        {staff.openUntouched > 0 && (
          <span className="text-destructive"> · {staff.openUntouched} open, untouched</span>
        )}
        {staff.contacted === 0 && staff.assigned > 0 && <span> · {staff.assigned} assigned</span>}
      </div>
    </div>
  )
}

function resultText(ev: FollowUpHistoryEventRow): string {
  if (ev.bookedAt) {
    return ev.bookedAppointmentAt ? `Booked · ${fmtDay(ev.bookedAppointmentAt)}` : 'Booked'
  }
  if (ev.repurchasedAt) {
    return ev.repurchasedAmount
      ? `Repurchased · ${formatCurrency(ev.repurchasedAmount)}`
      : 'Repurchased'
  }
  switch (ev.followUpStatus) {
    case 'not_interested':
      return 'Not interested · snoozed'
    case 'blacklisted':
      return 'Blacklisted'
    case 'expired':
      return 'Expired'
    case 'thinking':
      return 'Thinking it over'
    case 'callback':
      return 'Call back requested'
    case 'no_answer':
      return 'No answer'
    case 'replied':
      return 'Replied'
    default:
      return ''
  }
}

function ResultBadge({ ev }: { ev: FollowUpHistoryEventRow }) {
  const text = resultText(ev)
  if (!text) return <span className="text-text-light">—</span>
  const good = ev.bookedAt || ev.repurchasedAt
  const bad = ev.followUpStatus === 'not_interested' || ev.followUpStatus === 'blacklisted'
  return (
    <span
      className={cn(
        'inline-flex rounded px-1.5 py-0.5 text-[10.5px] font-extrabold uppercase tracking-wide',
        good
          ? 'bg-success/10 text-success'
          : bad
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-text-muted'
      )}
    >
      {text}
    </span>
  )
}
